/**
 * Fixed Assets register + depreciation/disposal — Phase 4.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, X, TrendingDown, XCircle, PlayCircle, Package } from 'lucide-react';
import { booksClient, getBooksTenant, errMsg } from './api';
import { toast } from '../hooks/use-toast';

const METHOD_LABEL = {
  straight_line: 'Straight-line',
  reducing_balance: 'Reducing balance',
  units_of_production: 'Units of production',
};

const fmt = (n, cur) => {
  const sym = { GBP: '£', INR: '₹', MYR: 'RM ', KZT: '₸', USD: '$', EUR: '€' }[cur] || '';
  return `${sym}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const FixedAssetsPage = () => {
  const cur = getBooksTenant()?.base_currency;
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [openDep, setOpenDep] = useState(null);
  const [openDispose, setOpenDispose] = useState(null);
  const [openBatch, setOpenBatch] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      booksClient.get('/fixed-assets'),
      booksClient.get('/ledger/accounts'),
    ]).then(([a, l]) => {
      setItems(a.data.data || []);
      setAccounts(l.data.data || []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">Assets</div>
          <h1 className="text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Fixed Assets Register</h1>
          <div className="text-sm text-[#0b1a2e]/60 mt-1">Straight-line · Reducing-balance · Units-of-production. All journals post automatically.</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setOpenBatch(true)} data-testid="fa-batch-btn"
            className="inline-flex items-center gap-1 px-4 py-2 border border-[#0f5c3d] text-[#0f5c3d] rounded-full text-sm font-semibold">
            <PlayCircle className="w-3.5 h-3.5" /> Monthly Depreciation Run
          </button>
          <button onClick={() => setOpenNew(true)} data-testid="fa-new-btn"
            className="inline-flex items-center gap-1 px-4 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold">
            <Plus className="w-3.5 h-3.5" /> New Asset
          </button>
        </div>
      </div>

      {loading ? <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> :
        items.length === 0 ? (
          <div className="p-12 text-center text-[#0b1a2e]/40 italic bg-white rounded-2xl border border-black/5">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No fixed assets registered yet.
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
                <tr>
                  <th className="text-left px-4 py-2">Code</th>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Method</th>
                  <th className="text-right px-4 py-2">Cost</th>
                  <th className="text-right px-4 py-2">Accum. Dep</th>
                  <th className="text-right px-4 py-2">Book Value</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.asset_id} className="border-t border-slate-100 hover:bg-[#f7fbf8]">
                    <td className="px-4 py-2 font-mono text-xs">{a.code}</td>
                    <td className="px-4 py-2">{a.name}<div className="text-[10px] text-[#0b1a2e]/50">{a.category || '—'}</div></td>
                    <td className="px-4 py-2 text-xs">{METHOD_LABEL[a.depreciation_method] || a.depreciation_method}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmt(a.cost, cur)}</td>
                    <td className="px-4 py-2 text-right font-mono text-[#0b1a2e]/70">{fmt(a.accumulated_depreciation, cur)}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">{fmt(a.book_value, cur)}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${a.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>{a.status}</span>
                    </td>
                    <td className="px-4 py-2 text-right space-x-1">
                      {a.status === 'active' && (
                        <>
                          <button onClick={() => setOpenDep(a)} title="Depreciate" data-testid={`fa-dep-${a.code}`}
                            className="text-blue-700 hover:text-blue-900"><TrendingDown className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setOpenDispose(a)} title="Dispose" data-testid={`fa-dispose-${a.code}`}
                            className="text-red-600 hover:text-red-800"><XCircle className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {openNew && <AssetEditor accounts={accounts} onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }} />}
      {openDep && <DepDialog asset={openDep} onClose={() => setOpenDep(null)} onSaved={() => { setOpenDep(null); load(); }} />}
      {openDispose && <DisposeDialog asset={openDispose} accounts={accounts} cur={cur} onClose={() => setOpenDispose(null)} onSaved={() => { setOpenDispose(null); load(); }} />}
      {openBatch && <BatchDialog onClose={() => setOpenBatch(false)} onSaved={() => { setOpenBatch(false); load(); }} />}
    </div>
  );
};

const AssetEditor = ({ accounts, onClose, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    code: '', name: '', category: '', notes: '',
    acquisition_date: today, cost: '', salvage_value: 0,
    depreciation_method: 'straight_line',
    useful_life_months: 36, reducing_balance_rate: 20,
    total_expected_units: '', units_uom: 'hours',
    asset_account_id: '', accum_dep_account_id: '', dep_expense_account_id: '',
    acquisition_bank_account_id: '', acquisition_creditor_id: '',
    payment_kind: 'cash',
  });

  const fixed = accounts.filter((a) => a.subtype === 'fixed_asset');
  const contra = accounts.filter((a) => a.subtype === 'contra_asset');
  const dep = accounts.filter((a) => a.subtype === 'depreciation_expense' || a.code === '6100');
  const banks = accounts.filter((a) => a.subtype === 'bank' || a.subtype === 'cash');
  const creditors = accounts.filter((a) => a.subtype === 'payable');

  // Default account selection
  useEffect(() => {
    setForm((f) => ({
      ...f,
      asset_account_id: f.asset_account_id || fixed[0]?.account_id || '',
      accum_dep_account_id: f.accum_dep_account_id || contra[0]?.account_id || '',
      dep_expense_account_id: f.dep_expense_account_id || dep[0]?.account_id || '',
      acquisition_bank_account_id: f.acquisition_bank_account_id || banks[0]?.account_id || '',
    }));
  }, [accounts.length]); // eslint-disable-line

  const submit = async () => {
    try {
      const payload = { ...form, cost: parseFloat(form.cost) || 0, salvage_value: parseFloat(form.salvage_value) || 0 };
      if (form.payment_kind === 'cash') { payload.acquisition_creditor_id = null; }
      else { payload.acquisition_bank_account_id = null; }
      if (form.depreciation_method !== 'units_of_production') {
        payload.total_expected_units = null;
      }
      if (form.depreciation_method === 'units_of_production') {
        payload.useful_life_months = null;
        payload.total_expected_units = parseFloat(form.total_expected_units) || 0;
      }
      await booksClient.post('/fixed-assets', payload);
      toast({ title: 'Asset registered — acquisition journal posted' });
      onSaved();
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[95vh] overflow-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>New Fixed Asset</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <L label="Code"><input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} data-testid="fa-code" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
          <L label="Name"><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="fa-name" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
          <L label="Category"><input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
          <L label="Acquisition date"><input type="date" value={form.acquisition_date} onChange={(e) => setForm((f) => ({ ...f, acquisition_date: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
          <L label="Cost"><input type="number" step="0.01" value={form.cost} onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))} data-testid="fa-cost" className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono" /></L>
          <L label="Salvage value"><input type="number" step="0.01" value={form.salvage_value} onChange={(e) => setForm((f) => ({ ...f, salvage_value: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono" /></L>

          <L label="Depreciation method">
            <select value={form.depreciation_method} onChange={(e) => setForm((f) => ({ ...f, depreciation_method: e.target.value }))} data-testid="fa-method" className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
              <option value="straight_line">Straight-line</option>
              <option value="reducing_balance">Reducing balance</option>
              <option value="units_of_production">Units of production</option>
            </select>
          </L>
          {form.depreciation_method !== 'units_of_production' && (
            <L label="Useful life (months)"><input type="number" value={form.useful_life_months} onChange={(e) => setForm((f) => ({ ...f, useful_life_months: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
          )}
          {form.depreciation_method === 'reducing_balance' && (
            <L label="Annual rate %"><input type="number" step="0.01" value={form.reducing_balance_rate} onChange={(e) => setForm((f) => ({ ...f, reducing_balance_rate: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
          )}
          {form.depreciation_method === 'units_of_production' && (
            <>
              <L label="Total expected units"><input type="number" value={form.total_expected_units} onChange={(e) => setForm((f) => ({ ...f, total_expected_units: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
              <L label="Units UoM"><input value={form.units_uom} onChange={(e) => setForm((f) => ({ ...f, units_uom: e.target.value }))} placeholder="hours / km / units" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
            </>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-xs font-semibold text-[#0b1a2e]/70 uppercase tracking-widest mb-3">Accounting mapping</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <L label="Asset account"><select value={form.asset_account_id} onChange={(e) => setForm((f) => ({ ...f, asset_account_id: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">{fixed.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} — {a.name}</option>)}</select></L>
            <L label="Accumulated depreciation account"><select value={form.accum_dep_account_id} onChange={(e) => setForm((f) => ({ ...f, accum_dep_account_id: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">{contra.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} — {a.name}</option>)}</select></L>
            <L label="Depreciation expense account"><select value={form.dep_expense_account_id} onChange={(e) => setForm((f) => ({ ...f, dep_expense_account_id: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">{dep.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} — {a.name}</option>)}</select></L>
            <L label="Purchase paid via">
              <select value={form.payment_kind} onChange={(e) => setForm((f) => ({ ...f, payment_kind: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                <option value="cash">Bank / Cash</option>
                <option value="credit">On credit (Creditor)</option>
              </select>
            </L>
            {form.payment_kind === 'cash' ? (
              <L label="Bank / Cash account"><select value={form.acquisition_bank_account_id} onChange={(e) => setForm((f) => ({ ...f, acquisition_bank_account_id: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">{banks.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} — {a.name}</option>)}</select></L>
            ) : (
              <L label="Creditor account"><select value={form.acquisition_creditor_id} onChange={(e) => setForm((f) => ({ ...f, acquisition_creditor_id: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">{creditors.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} — {a.name}</option>)}</select></L>
            )}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Cancel</button>
          <button onClick={submit} disabled={!form.code || !form.name || !form.cost} data-testid="fa-save"
            className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-50">Register & Post</button>
        </div>
      </div>
    </div>
  );
};

const DepDialog = ({ asset, onClose, onSaved }) => {
  const today = new Date();
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [form, setForm] = useState({ period_end: lastDay, units_used: '' });

  const submit = async () => {
    try {
      const payload = { period_end: form.period_end };
      if (asset.depreciation_method === 'units_of_production') {
        payload.units_used = parseFloat(form.units_used) || 0;
      }
      await booksClient.post(`/fixed-assets/${asset.asset_id}/depreciate`, payload);
      toast({ title: 'Depreciation journal posted' });
      onSaved();
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Depreciate {asset.code}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <L label="Period end (last day of month)"><input type="date" value={form.period_end} onChange={(e) => setForm((f) => ({ ...f, period_end: e.target.value }))} data-testid="fa-dep-date" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
          {asset.depreciation_method === 'units_of_production' && (
            <L label={`Units used this period (${asset.units_uom || 'units'})`}>
              <input type="number" step="0.01" value={form.units_used} onChange={(e) => setForm((f) => ({ ...f, units_used: e.target.value }))} data-testid="fa-dep-units" className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono" />
            </L>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Cancel</button>
          <button onClick={submit} data-testid="fa-dep-save"
            className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold">Post Depreciation</button>
        </div>
      </div>
    </div>
  );
};

const DisposeDialog = ({ asset, accounts, cur, onClose, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const banks = accounts.filter((a) => a.subtype === 'bank' || a.subtype === 'cash');
  const [form, setForm] = useState({ disposal_date: today, proceeds: 0, bank_account_id: banks[0]?.account_id || '' });
  const submit = async () => {
    try {
      await booksClient.post(`/fixed-assets/${asset.asset_id}/dispose`, { ...form, proceeds: parseFloat(form.proceeds) || 0 });
      toast({ title: 'Asset disposed — journal posted' });
      onSaved();
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Dispose {asset.code}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="text-xs text-[#0b1a2e]/60 mb-3 bg-[#f7fbf8] rounded p-3 border border-[#0f5c3d]/10">
          Current book value: <strong>{fmt(asset.book_value, cur)}</strong>
        </div>
        <div className="space-y-3">
          <L label="Disposal date"><input type="date" value={form.disposal_date} onChange={(e) => setForm((f) => ({ ...f, disposal_date: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
          <L label="Sale proceeds"><input type="number" step="0.01" value={form.proceeds} onChange={(e) => setForm((f) => ({ ...f, proceeds: e.target.value }))} data-testid="fa-dispose-proceeds" className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono" /></L>
          <L label="Bank / Cash received"><select value={form.bank_account_id} onChange={(e) => setForm((f) => ({ ...f, bank_account_id: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">{banks.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} — {a.name}</option>)}</select></L>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Cancel</button>
          <button onClick={submit} disabled={!form.bank_account_id} data-testid="fa-dispose-save"
            className="px-5 py-2 bg-red-600 text-white rounded-full text-sm font-semibold disabled:opacity-50">Dispose</button>
        </div>
      </div>
    </div>
  );
};

const BatchDialog = ({ onClose, onSaved }) => {
  const today = new Date();
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [periodEnd, setPeriodEnd] = useState(lastDay);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const run = async () => {
    setRunning(true);
    try {
      const r = await booksClient.post('/fixed-assets/depreciate-batch', { period_end: periodEnd });
      setResult(r.data);
      toast({ title: `Batch complete — posted ${r.data.posted} of ${r.data.assets_considered}` });
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
    finally { setRunning(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Monthly Depreciation Run</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-[#0b1a2e]/60 mb-3">
          Posts one monthly depreciation journal per active straight-line / reducing-balance asset.
          Units-of-production assets are skipped (need per-asset input).
        </p>
        <L label="Period end (last day of month)"><input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} data-testid="fa-batch-date" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
        {result && (
          <div className="mt-4 max-h-64 overflow-auto text-xs">
            <div className="mb-2 text-[#0b1a2e]/70">Considered: <strong>{result.assets_considered}</strong> · Posted: <strong className="text-emerald-700">{result.posted}</strong></div>
            <table className="w-full">
              <thead className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/50"><tr><th className="text-left py-1">Code</th><th className="text-left py-1">Status</th><th className="text-right py-1">Depreciation</th></tr></thead>
              <tbody>
                {result.results.map((r) => (
                  <tr key={r.asset_id} className="border-t border-slate-100">
                    <td className="py-1 font-mono">{r.code}</td>
                    <td className="py-1">{r.status}{r.error ? ` (${r.error})` : ''}</td>
                    <td className="py-1 text-right font-mono">{r.depreciation != null ? Number(r.depreciation).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={() => { onSaved(); }} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Close</button>
          <button onClick={run} disabled={running} data-testid="fa-batch-run"
            className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-50">
            {running ? 'Running…' : 'Run'}
          </button>
        </div>
      </div>
    </div>
  );
};

const L = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-1">{label}</div>
    {children}
  </label>
);

export default FixedAssetsPage;
